import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowUpRight, Loader2, CheckCircle2, Globe2, Building2, Cog } from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";
import { toast, Toaster } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INQUIRY_OPTIONS = [
  "I represent a municipality or public institution",
  "I represent an organized industrial zone or business park",
  "I need electronic document and records management",
  "I need utility subscriber management",
  "I need finance, revenue or procurement modules",
  "I need field, infrastructure or spatial operations support",
  "I want to discuss portals, integrations or decision support",
  "I want to discuss localization for my country",
  "I want to discuss institutional system implementation",
  "I represent a structured organization",
  "Other",
];

const PREFERRED_CONTACT_OPTIONS = [
  "No preference",
  "Email",
  "Phone",
  "Video meeting",
];

const initialState = {
  full_name: "",
  email: "",
  organization: "",
  country: "",
  inquiry_type: INQUIRY_OPTIONS[0],
  message: "",
  role: "",
  phone: "",
  website: "",
  preferred_contact_method: "No preference",
  consent: true,
  marketing_consent: false,
};

const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Valid email required";
    if (!form.organization.trim()) e.organization = "Required";
    if (!form.country.trim()) e.country = "Required";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please provide at least a short message (10+ characters)";
    if (!form.consent) e.consent = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Drop "No preference" sentinel before sending
      const payload = {
        ...form,
        preferred_contact_method:
          form.preferred_contact_method === "No preference"
            ? null
            : form.preferred_contact_method,
      };
      await axios.post(`${API}/contact`, payload);
      setSuccess(true);
      setForm(initialState);
      toast.success("Inquiry received", {
        description: "Allinstra will review your message and respond where appropriate.",
      });
    } catch (err) {
      toast.error("Submission failed", {
        description:
          err?.response?.data?.detail ||
          "Your message could not be sent. Please try again or contact us directly at info@allinstra.com.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="page-contact">
      <Toaster position="top-right" richColors />
      <PageHero
        overline="Contact"
        title="Let’s discuss your institutional system needs."
        lead="Use this page to contact Allinstra about institutional systems, product portfolio questions, implementation planning, market adaptation, or structured organization needs."
      >
        <p className="max-w-3xl text-[15px] text-ink-600 leading-relaxed">
          Allinstra evaluates each inquiry according to institutional context, local
          infrastructure, technical feasibility, and implementation priorities.
        </p>
      </PageHero>

      <SectionWrapper id="contact-grid">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 space-y-6">
            <div className="border border-ink-200 p-6 bg-white">
              <div className="tag-overline">Email</div>
              <a
                href="mailto:info@allinstra.com"
                data-testid="contact-email"
                className="mt-3 flex items-center gap-2 font-display text-lg text-ink-900 hover:text-cyan-700 transition-colors"
              >
                <Mail size={16} className="text-cyan-600" /> info@allinstra.com
              </a>
            </div>

            <div className="border border-ink-200 p-6 bg-white">
              <div className="tag-overline">Website</div>
              <div className="mt-3 flex items-center gap-2 font-display text-lg text-ink-900">
                <Globe2 size={16} className="text-cyan-600" /> allinstra.com
              </div>
            </div>

            <div className="border border-ink-200 p-6 bg-white">
              <div className="tag-overline">Brand</div>
              <div className="mt-3 text-sm text-ink-700">
                Allinstra is an institutional systems brand by{" "}
                <span className="text-ink-900 font-medium">Erenown</span>.
              </div>
              <Link
                to="/company-information"
                className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-cyan-700 hover:text-cyan-800"
              >
                Company information <ArrowUpRight size={12} />
              </Link>
            </div>

            <div className="border border-ink-200 p-6 bg-white">
              <div className="tag-overline">Address</div>
              <div className="mt-3 flex items-start gap-2 text-sm text-ink-700">
                <MapPin size={16} className="text-cyan-600 mt-0.5" />
                <div>
                  <div className="font-medium text-ink-900">Erenown LLC</div>
                  <div className="text-ink-600 mt-1">
                    Bulevardi Dëshmorët e Kombit, C2, HIII, Nr.6
                    <br />
                    Prishtinë, Republic of Kosovo
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <section className="lg:col-span-8">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="border border-ink-200 bg-white p-7 sm:p-10"
              noValidate
            >
              {success ? (
                <div data-testid="contact-success" className="flex flex-col items-start gap-4 py-12">
                  <CheckCircle2 size={28} className="text-cyan-600" />
                  <h2 className="heading-display text-2xl sm:text-3xl font-semibold">
                    Thank you. Your inquiry has been received.
                  </h2>
                  <p className="text-ink-600 max-w-xl">
                    Allinstra will review your message and respond where appropriate. For any
                    follow-up, please email{" "}
                    <a href="mailto:info@allinstra.com" className="text-cyan-700 underline">
                      info@allinstra.com
                    </a>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="contact-send-another"
                    className="btn-secondary"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <div className="tag-overline">Inquiry</div>
                  <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
                    Tell us about your scope.
                  </h2>

                  {/* Required fields */}
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Full Name *" testId="field-full-name" error={errors.full_name}>
                      <input
                        type="text"
                        value={form.full_name}
                        onChange={(e) => setField("full_name", e.target.value)}
                        className={inputCls(errors.full_name)}
                        placeholder="Your full name"
                        required
                        data-testid="input-full-name"
                      />
                    </Field>
                    <Field label="Business Email *" testId="field-email" error={errors.email}>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        className={inputCls(errors.email)}
                        placeholder="name@institution.org"
                        required
                        data-testid="input-email"
                      />
                    </Field>
                    <Field
                      label="Organization / Institution Name *"
                      testId="field-organization"
                      error={errors.organization}
                    >
                      <input
                        type="text"
                        value={form.organization}
                        onChange={(e) => setField("organization", e.target.value)}
                        className={inputCls(errors.organization)}
                        placeholder="Municipality, zone, institution…"
                        required
                        data-testid="input-organization"
                      />
                    </Field>
                    <Field
                      label="Country / Market *"
                      testId="field-country"
                      error={errors.country}
                    >
                      <input
                        type="text"
                        value={form.country}
                        onChange={(e) => setField("country", e.target.value)}
                        className={inputCls(errors.country)}
                        placeholder="Kosovo, Türkiye, UK, EU, …"
                        required
                        data-testid="input-country"
                      />
                    </Field>
                  </div>

                  {/* Inquiry type */}
                  <div className="mt-5">
                    <Field label="Inquiry Type *" testId="field-inquiry">
                      <select
                        value={form.inquiry_type}
                        onChange={(e) => setField("inquiry_type", e.target.value)}
                        className={inputCls()}
                        data-testid="input-inquiry-type"
                      >
                        {INQUIRY_OPTIONS.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  {/* Optional fields */}
                  <div className="mt-7 pt-6 border-t border-ink-200">
                    <div className="tag-overline">Optional details</div>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="Role / Title" testId="field-role">
                        <input
                          type="text"
                          value={form.role}
                          onChange={(e) => setField("role", e.target.value)}
                          className={inputCls()}
                          placeholder="e.g. CIO, Director, Manager"
                          data-testid="input-role"
                        />
                      </Field>
                      <Field label="Phone Number" testId="field-phone">
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setField("phone", e.target.value)}
                          className={inputCls()}
                          placeholder="+ country code"
                          data-testid="input-phone"
                        />
                      </Field>
                      <Field label="Website" testId="field-website">
                        <input
                          type="url"
                          value={form.website}
                          onChange={(e) => setField("website", e.target.value)}
                          className={inputCls()}
                          placeholder="https://institution.org"
                          data-testid="input-website"
                        />
                      </Field>
                      <Field
                        label="Preferred Contact Method"
                        testId="field-preferred-contact"
                      >
                        <select
                          value={form.preferred_contact_method}
                          onChange={(e) => setField("preferred_contact_method", e.target.value)}
                          className={inputCls()}
                          data-testid="input-preferred-contact"
                        >
                          {PREFERRED_CONTACT_OPTIONS.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mt-5">
                    <Field label="Message *" testId="field-message" error={errors.message}>
                      <textarea
                        value={form.message}
                        onChange={(e) => setField("message", e.target.value)}
                        rows={6}
                        className={inputCls(errors.message)}
                        placeholder="Briefly describe your institution, market, scope and any specific modules you would like to discuss."
                        required
                        data-testid="input-message"
                      />
                    </Field>
                  </div>

                  {/* Consent */}
                  <label className="mt-5 flex items-start gap-3 text-sm text-ink-600">
                    <input
                      type="checkbox"
                      checked={form.consent}
                      onChange={(e) => setField("consent", e.target.checked)}
                      className="mt-1 h-4 w-4 accent-cyan-600"
                      data-testid="input-consent"
                    />
                    <span>
                      I agree that Allinstra (operated by Erenown) may store and process this
                      submission for the purpose of responding to my inquiry. See our{" "}
                      <Link to="/privacy" className="underline hover:text-cyan-700">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>
                  {errors.consent && (
                    <div className="text-xs text-red-600 mt-1">{errors.consent}</div>
                  )}

                  {/* Marketing consent (optional, not pre-checked) */}
                  <label className="mt-3 flex items-start gap-3 text-sm text-ink-600">
                    <input
                      type="checkbox"
                      checked={form.marketing_consent}
                      onChange={(e) => setField("marketing_consent", e.target.checked)}
                      className="mt-1 h-4 w-4 accent-cyan-600"
                      data-testid="input-marketing-consent"
                    />
                    <span>
                      I would like to receive occasional updates about Allinstra solutions,
                      institutional systems, resources, and business opportunities. I understand
                      that I can unsubscribe at any time.
                    </span>
                  </label>

                  {/* Privacy notice */}
                  <div
                    data-testid="contact-privacy-notice"
                    className="mt-6 text-xs leading-relaxed text-ink-500 border-t border-ink-200 pt-5"
                  >
                    Allinstra is an institutional systems brand by Erenown. Your information
                    will be processed primarily by Erenown LLC to respond to your request,
                    assess institutional system interest, and manage business communications.
                    Where relevant to your request, data may also be reviewed or handled by
                    Erenown Ltd or another company under the same ultimate beneficial ownership
                    and control. For more information, please read our{" "}
                    <Link to="/privacy" className="underline hover:text-cyan-700">
                      Privacy Policy
                    </Link>
                    .
                  </div>

                  {/* Submit */}
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Send Inquiry <ArrowUpRight size={16} />
                        </>
                      )}
                    </button>
                    <span className="text-xs text-ink-500">
                      Submissions are stored securely and reviewed by the Allinstra team.
                    </span>
                  </div>
                </>
              )}
            </form>
          </section>
        </div>
      </SectionWrapper>

      {/* Guidance cards */}
      <SectionWrapper id="contact-guidance" alt>
        <SectionHeader
          overline="When to contact Allinstra"
          title="Three common starting points."
        />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Building2,
              tag: "For Institutions",
              testId: "guidance-institutions",
              body:
                "Contact Allinstra if you need to evaluate municipal systems, public-service workflows, industrial zone operations, document management, finance workflows, or institutional reporting needs.",
            },
            {
              icon: Globe2,
              tag: "For Market Discussions",
              testId: "guidance-markets",
              body:
                "Contact Allinstra if you want to discuss whether the portfolio can be localized or adapted for your country, region, institution, or market opportunity.",
            },
            {
              icon: Cog,
              tag: "For Implementation Planning",
              testId: "guidance-implementation",
              body:
                "Contact Allinstra if you need to discuss module selection, phased rollout, integration feasibility, user roles, document flows, or local infrastructure requirements.",
            },
          ].map((g) => (
            <div
              key={g.testId}
              data-testid={g.testId}
              className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col"
            >
              <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <g.icon size={18} strokeWidth={1.5} />
              </div>
              <div className="tag-overline mt-5">{g.tag}</div>
              <p className="mt-3 text-[14.5px] leading-relaxed text-ink-700">{g.body}</p>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* Final CTA */}
      <SectionWrapper id="contact-final-cta">
        <div className="border border-ink-200 p-8 sm:p-12 bg-white">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="tag-overline">Final step</div>
              <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
                Start with a structured conversation.
              </h2>
              <p className="mt-3 text-[15px] text-ink-600 leading-relaxed max-w-2xl">
                Allinstra helps institutions and structured organizations evaluate the right
                scope, module combination, and implementation path according to real
                operational conditions.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <a
                href="#contact-grid"
                data-testid="contact-final-cta"
                className="btn-primary"
              >
                Send Inquiry <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </SectionWrapper>
    </main>
  );
};

const inputCls = (error) =>
  `w-full bg-white border ${
    error ? "border-red-400" : "border-ink-300"
  } px-4 py-3 text-[15px] text-ink-900 placeholder:text-ink-400 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors rounded-none`;

const Field = ({ label, testId, error, children }) => (
  <div data-testid={testId}>
    <label className="font-mono text-[10px] uppercase tracking-widest text-ink-500 block mb-2">
      {label}
    </label>
    {children}
    {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
  </div>
);

export default Contact;
